import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';
import { Tag } from '../primitives/Badge.js';
import { PulseDot, RouteProgress } from '../primitives/Feedback.js';
import { useTheme, type Density, type ThemeMode } from '../theme/ThemeProvider.js';
import { formatRate } from '../lib/format.js';


export interface NavItem {
  key: string;
  label: string;
  tag?: string;
  badge?: number;
  href?: string;
  permission?: string;
}

export interface NavModule {
  key: string;
  label: string;
  hint?: string;
  items: NavItem[];
  href?: string;
}

export interface CompanyBrand {
  name: string;
  tagline?: string;
  mark?: string;
  logoUrl?: string;
  timezone?: string;
}

export interface CurrentUser {
  name: string;
  initials: string;
  jobTitle?: string;
}

export type EntryCurrency = string;

export interface CurrencyOption {
  code: string;
  name: string;
  symbol?: string | null;
  isBase: boolean;
  rate: string | null;
}

export interface FxControl {
  currency: EntryCurrency;
  currencies: CurrencyOption[];
  rate: string;
  onCurrencyChange: (currency: EntryCurrency) => void;
  onRateChange: (rate: string) => void;
  canEditRate?: boolean;
  onApplyRate?: (currency: EntryCurrency, rate: string) => Promise<void>;
}

export interface BranchControl {
  options: Array<{ code: string; name: string }>;
  active: string;
  onChange: (code: string) => void;
  canSwitch: boolean;
}

function isPlainClick(event: ReactMouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}


export interface SearchHit {
  id: string;
  label: string;
  hint?: string;
  group: string;
}

export interface SearchControl {
  find: (query: string) => Promise<SearchHit[]> | SearchHit[];
  onSelect: (hit: SearchHit) => void;
  placeholder?: string;
}

export interface TopBarProps {
  brand: CompanyBrand;
  modules: NavModule[];
  activeModule: string;
  onModuleChange: (key: string) => void;
  user: CurrentUser;
  search?: SearchControl;
  fx?: FxControl;
  branch?: BranchControl;
  roles?: Array<{ value: string; label: string; hint?: string }>;
  activeRole?: string;
  onRoleChange?: (role: string) => void;
  onSignOut?: () => void;
}

export function TopBar({
  brand,
  modules,
  activeModule,
  onModuleChange,
  user,
  search,
  fx,
  branch,
  roles,
  activeRole,
  onRoleChange,
  onSignOut,
}: TopBarProps) {
  const { resolvedTheme, setMode, density, setDensity } = useTheme();

  return (
    <header
      data-noprint="1"
      className="z-20 flex h-[var(--header-h)] shrink-0 items-center gap-3.5 border-b border-[var(--line)] bg-[var(--surface)] px-4"
    >
      {}
      <div className="flex shrink-0 items-center gap-2.5">
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt=""
            className="size-[26px] rounded-[7px] bg-[var(--surface-2)] object-contain"
          />
        ) : (
          <div className="grid size-[26px] place-items-center rounded-[7px] bg-[var(--accent-solid)] text-[13px] font-bold tracking-tight text-[var(--accent-ink)]">
            {brand.mark ?? brand.name.charAt(0)}
          </div>
        )}
        <div className="flex flex-col leading-[1.05]">
          <span className="text-[14px] font-semibold tracking-tight whitespace-nowrap">
            {brand.name}
          </span>
          {brand.timezone ? (
            <Clock timezone={brand.timezone} />
          ) : (
            brand.tagline && (
              <span className="bb-eyebrow text-[9.5px] tracking-[0.14em] whitespace-nowrap">
                {brand.tagline}
              </span>
            )
          )}
        </div>
      </div>

      {}
      <nav
        aria-label="Modules"
        className="hidden h-full min-w-0 flex-[0_1_auto] items-stretch gap-0.5 overflow-x-auto lg:flex [scrollbar-width:none]"
      >
        {modules.map((m) => {
          const active = m.key === activeModule;
          const className = cn(
            'relative flex h-full shrink-0 cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3',
            'text-[13px] font-medium whitespace-nowrap no-underline transition-colors',
            active
              ? 'text-[var(--ink)]'
              : 'text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
          );
          const inner = (
            <>
              {m.label}
              <span
                aria-hidden="true"
                className="absolute right-3 bottom-0 left-3 h-0.5 origin-left rounded-t-[2px] bg-[var(--accent)] transition-transform duration-[var(--dur)] ease-[var(--ease)]"
                style={{ transform: `scaleX(${active ? 1 : 0})` }}
              />
            </>
          );

          return m.href ? (
            <a
              key={m.key}
              href={m.href}
              title={m.hint}
              aria-current={active ? 'page' : undefined}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                onModuleChange(m.key);
              }}
              className={className}
            >
              {inner}
            </a>
          ) : (
            <button
              key={m.key}
              type="button"
              title={m.hint}
              aria-current={active ? 'page' : undefined}
              onClick={() => onModuleChange(m.key)}
              className={className}
            >
              {inner}
            </button>
          );
        })}
      </nav>

      <div className="flex-1" />

      {}
      {search && <GlobalSearch {...search} modules={modules} />}

      {}
      {branch && branch.options.length > 1 && <BranchMenu {...branch} />}

      {}
      {fx && <FxMenu {...fx} />}

      {}
      <UserMenu
        user={user}
        roles={roles}
        activeRole={activeRole}
        onRoleChange={onRoleChange}
        onSignOut={onSignOut}
        theme={resolvedTheme}
        onThemeChange={setMode}
        density={density}
        onDensityChange={setDensity}
      />
    </header>
  );
}

function Clock({ timezone }: { timezone: string }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const align = setTimeout(
      () => {
        setNow(new Date());
        interval = setInterval(() => setNow(new Date()), 60_000);
      },
      (60 - new Date().getSeconds()) * 1000,
    );
    return () => {
      clearTimeout(align);
      if (interval) clearInterval(interval);
    };
  }, []);

  const text = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(now);

  return (
    <time
      dateTime={now.toISOString()}
      title={`Company time · ${timezone}`}
      className="bb-num text-[9.5px] whitespace-nowrap text-[var(--ink-3)]"
    >
      {text}
    </time>
  );
}


const SEARCH_DEBOUNCE_MS = 160;

function GlobalSearch({
  find,
  onSelect,
  placeholder = 'Search accounts, vouchers, parties…',
  modules,
}: SearchControl & { modules: NavModule[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const latest = useRef(0);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable === true;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setQuery('');
      setHits([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const term = query.trim();
    const navHits: SearchHit[] = term
      ? modules.flatMap((module) =>
          module.items
            .filter((item) =>
              `${module.label} ${item.label}`.toLowerCase().includes(term.toLowerCase()),
            )
            .map((item) => ({
              id: `nav::${item.key}`,
              label: item.label,
              hint: module.label,
              group: 'Go to',
            })),
        )
      : [];

    if (!term) {
      setHits([]);
      return;
    }

    const attempt = ++latest.current;
    setHits(navHits);
    setBusy(true);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const found = await find(term);
          if (attempt !== latest.current) return;
          setHits([...navHits, ...found]);
        } catch {
          if (attempt === latest.current) setHits(navHits);
        } finally {
          if (attempt === latest.current) setBusy(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, open, find, modules]);

  useEffect(() => {
    setActive((index) => (index < hits.length ? index : 0));
  }, [hits.length]);

  const choose = (hit: SearchHit | undefined) => {
    if (!hit) return;
    onSelect(hit);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        title="Search — Ctrl+K"
        className={cn(
          'flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] border px-2.5',
          'border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-3)]',
          'transition-colors hover:border-[var(--accent)] hover:text-[var(--ink)]',
        )}
      >
        <SearchIcon />
        <span className="hidden text-[12px] xl:inline">Search</span>
        <kbd
          className={cn(
            'hidden rounded-[4px] border border-[var(--line)] bg-[var(--surface)] px-1',
            'text-[10px] text-[var(--ink-3)] xl:inline',
          )}
        >
          Ctrl K
        </kbd>
      </button>

      {open &&
        createPortal(
          <div
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
            className="fixed inset-0 z-[100] flex justify-center bg-black/40 px-4 pt-[12vh]"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Search"
              className={cn(
                'flex max-h-[70vh] w-full max-w-[600px] flex-col overflow-hidden',
                'rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface)]',
                'shadow-[0_24px_64px_rgba(0,0,0,0.32)]',
              )}
            >
              <div className="flex items-center gap-2.5 border-b border-[var(--line)] px-3.5">
                <SearchIcon />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActive(0);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setOpen(false);
                    else if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      setActive((i) => (hits.length === 0 ? 0 : (i + 1) % hits.length));
                    } else if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      setActive((i) =>
                        hits.length === 0 ? 0 : (i - 1 + hits.length) % hits.length,
                      );
                    } else if (event.key === 'Enter') {
                      event.preventDefault();
                      choose(hits[active]);
                    }
                  }}
                  placeholder={placeholder}
                  aria-label="Search"
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(
                    'h-12 w-full border-0 bg-transparent text-[14px] text-[var(--ink)] outline-none',
                    'placeholder:text-[var(--ink-3)]',
                  )}
                />
                {busy && <PulseDot />}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {query.trim() === '' ? (
                  <p className="m-0 px-3.5 py-3 text-[12.5px] text-[var(--ink-3)]">
                    Type an account code or name, a voucher number, a party, or the name of a
                    screen. ↑↓ to move, Enter to open.
                  </p>
                ) : hits.length === 0 ? (
                  <p className="m-0 px-3.5 py-3 text-[12.5px] text-[var(--ink-3)]">
                    {busy ? 'Searching…' : `Nothing matches “${query.trim()}”.`}
                  </p>
                ) : (
                  hits.map((hit, index) => {
                    const heading = hit.group !== hits[index - 1]?.group;
                    return (
                      <div key={hit.id}>
                        {heading && (
                          <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
                            {hit.group}
                          </div>
                        )}
                        <button
                          type="button"
                          onMouseEnter={() => setActive(index)}
                          onClick={() => choose(hit)}
                          className={cn(
                            'flex w-full cursor-pointer items-baseline gap-2.5 border-0 px-3.5 py-2 text-left',
                            index === active ? 'bg-[var(--accent-soft)]' : 'bg-transparent',
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--ink)]">
                            {hit.label}
                          </span>
                          {hit.hint && (
                            <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                              {hit.hint}
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.5 8.5L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}


function Menu({
  trigger,
  title,
  children,
  width = 268,
}: {
  trigger: ReactNode;
  title: string;
  children: ReactNode;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title={title}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-[var(--radius-sm)] border px-2.5 py-[5px]',
          'transition-colors duration-[var(--dur-fast)]',
          open
            ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
            : 'border-[var(--line)] bg-[var(--surface-2)] hover:border-[var(--ink-3)]',
        )}
      >
        {trigger}
        <svg
          width="9"
          height="6"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
          className="text-[var(--ink-3)]"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={title}
          style={{ width }}
          className="bb-card absolute top-[calc(100%+7px)] right-0 z-30 flex flex-col overflow-hidden shadow-[var(--shadow-lg)]"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuOption({
  selected,
  onSelect,
  label,
  hint,
  trailing,
}: {
  selected: boolean;
  onSelect: () => void;
  label: ReactNode;
  hint?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 border-0 px-3 py-2 text-left',
        selected ? 'bg-[var(--accent-soft)]' : 'bg-transparent hover:bg-[var(--surface-2)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-[15px] shrink-0 place-items-center rounded-full border',
          selected
            ? 'border-[var(--accent-solid)] bg-[var(--accent-solid)]'
            : 'border-[var(--line)]',
        )}
      >
        {selected && <span className="size-[5px] rounded-full bg-[var(--accent-ink)]" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-medium text-[var(--ink)]">{label}</span>
        {hint && <span className="block text-[11px] text-[var(--ink-3)]">{hint}</span>}
      </span>
      {trailing}
    </button>
  );
}

function FxMenu({
  currency,
  currencies,
  rate,
  onCurrencyChange,
  onRateChange,
  canEditRate = false,
  onApplyRate,
}: FxControl) {
  const base = currencies.find((c) => c.isBase);
  const baseCode = base?.code ?? currency;
  const onBase = currency === baseCode;

  const stored = currencies.find((c) => c.code === currency)?.rate ?? null;
  const dirty = rate.trim() !== '' && rate.trim() !== (stored ?? '').trim();

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  const applyRate = () => {
    if (!onApplyRate) return;
    setApplying(true);
    setApplyError(null);
    setApplied(null);
    onApplyRate(currency, rate.trim()).then(
      () => {
        setApplying(false);
        setApplied(rate.trim());
      },
      (cause: unknown) => {
        setApplying(false);
        setApplyError(cause instanceof Error ? cause.message : String(cause));
      },
    );
  };

  return (
    <Menu
      title="Entry currency and FX rate"
      trigger={
        <>
          {}
          <span className="bb-num text-[12px] font-medium whitespace-nowrap">
            {onBase ? `${baseCode} 1` : `${currency} 1 = ${baseCode} ${formatRate(rate)}`}
          </span>
          <PulseDot />
        </>
      }
    >
      <div className="bb-card-head">
        <span className="bb-eyebrow text-[10px]">Enter vouchers in</span>
      </div>

      {currencies.map((option) => (
        <MenuOption
          key={option.code}
          selected={currency === option.code}
          onSelect={() => onCurrencyChange(option.code)}
          label={
            option.isBase ? `${option.code} — base currency` : `${option.code} — ${option.name}`
          }
          hint={
            option.isBase
              ? 'Rate is always 1'
              : option.rate
                ? `1 ${option.code} = ${formatRate(option.rate)} ${baseCode}`
                :
                  `No ${option.code}/${baseCode} rate yet — set one in Setup → Tax & FX`
          }
        />
      ))}

      {}
      {!onBase && (
        <div className="flex items-center gap-2 border-t border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2.5">
          <label htmlFor="fx-rate" className="bb-eyebrow shrink-0 text-[10px] whitespace-nowrap">
            {currency} rate
          </label>
          <input
            id="fx-rate"
            value={rate}
            readOnly={!canEditRate}
            inputMode="decimal"
            onChange={(event) => {
              setApplied(null);
              setApplyError(null);
              onRateChange(event.target.value);
            }}
            title={
              canEditRate
                ? `${currency} → ${baseCode}`
                : 'Only an administrator can change the rate'
            }
            className={cn(
              'bb-num h-7 w-full rounded-[var(--radius-sm)] border border-[var(--line)] px-2 text-right text-[12.5px]',
              'outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]',
              canEditRate
                ? 'bg-[var(--surface)] text-[var(--ink)]'
                : 'cursor-not-allowed bg-[var(--surface-2)] text-[var(--ink-3)]',
            )}
          />

          {}
          {canEditRate && onApplyRate && (
            <button
              type="button"
              disabled={!dirty || applying}
              onClick={applyRate}
              title={
                dirty
                  ? `Save 1 ${currency} = ${rate} ${baseCode} as today's spot rate`
                  : `${currency} is already at this rate`
              }
              className={cn(
                'shrink-0 cursor-pointer rounded-[var(--radius-sm)] border px-2 py-[5px]',
                'text-[11.5px] font-medium whitespace-nowrap transition-colors',
                dirty && !applying
                  ? 'border-[var(--accent-solid)] bg-[var(--accent-solid)] text-[var(--accent-ink)]'
                  : 'cursor-not-allowed border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-3)]',
              )}
            >
              {applying ? 'Saving…' : 'Update'}
            </button>
          )}
        </div>
      )}

      {applyError && (
        <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--debit)]">
          {applyError}
        </p>
      )}

      {applied && !applyError && (
        <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-2)]">
          1 {currency} = {applied} {baseCode} saved as today’s SPOT rate. Every rate field in the
          application opens on it, and Setup → Tax &amp; FX lists it by date. Vouchers already
          posted keep the rate they were posted at.
        </p>
      )}

      <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-3)]">
        {onBase
          ? `${baseCode} is the reporting currency. Every voucher is stored in it as well as in the currency it was entered in.`
          : canEditRate
            ? onApplyRate
              ? 'Typing changes this browser only. Update makes it the book’s rate — a SPOT row dated today, which every screen reads. base = txn × rate.'
              : 'Applies to every voucher entered from now on. base = txn × rate.'
            : 'Applies to every voucher you enter. Ask an administrator to change the rate.'}
      </p>
    </Menu>
  );
}

function branchLabel(branch: { code: string; name: string } | undefined, fallback: string): string {
  if (!branch) return fallback;
  const name = branch.name.trim();
  if (!name) return branch.code;
  return name.toLowerCase().startsWith(branch.code.toLowerCase())
    ? name
    : `${branch.code} — ${name}`;
}

function BranchMenu({ options, active, onChange, canSwitch }: BranchControl) {
  const current = options.find((b) => b.code === active);
  const label = branchLabel(current, active);

  if (!canSwitch) {
    return (
      <div
        title="Your branch. An administrator can grant you access to others."
        className="flex shrink-0 items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface-2)] px-2.5 py-[5px]"
      >
        <span className="text-[12px] font-medium whitespace-nowrap text-[var(--ink-2)]">
          {label}
        </span>
      </div>
    );
  }

  return (
    <Menu
      title="Switch branch"
      trigger={<span className="text-[12px] font-medium whitespace-nowrap">{label}</span>}
    >
      <div className="bb-card-head">
        <span className="bb-eyebrow text-[10px]">Posting into</span>
      </div>
      {options.map((option) => (
        <MenuOption
          key={option.code}
          selected={option.code === active}
          onSelect={() => onChange(option.code)}
          label={option.name}
          trailing={<Tag>{option.code}</Tag>}
        />
      ))}
      <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-3)]">
        Switching branch changes which series a voucher number comes from.
      </p>
    </Menu>
  );
}

function MenuSegment<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string; hint?: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="bb-eyebrow shrink-0 text-[10px]">{label}</span>
      <div className="ml-auto flex overflow-hidden rounded-[var(--radius-sm)] border border-[var(--line)]">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={option.hint}
              onClick={() => onChange(option.value)}
              className={cn(
                'cursor-pointer border-0 px-2.5 py-[5px] text-[11.5px] font-medium whitespace-nowrap',
                'transition-colors duration-[var(--dur-fast)]',
                selected
                  ? 'bg-[var(--accent-solid)] text-[var(--accent-ink)]'
                  : 'bg-[var(--surface-2)] text-[var(--ink-2)] hover:text-[var(--ink)]',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserMenu({
  user,
  roles,
  activeRole,
  onRoleChange,
  onSignOut,
  theme,
  onThemeChange,
  density,
  onDensityChange,
}: {
  user: CurrentUser;
  roles?: Array<{ value: string; label: string; hint?: string }>;
  activeRole?: string;
  onRoleChange?: (role: string) => void;
  onSignOut?: () => void;
  theme: 'light' | 'dark';
  onThemeChange: (mode: ThemeMode) => void;
  density: Density;
  onDensityChange: (density: Density) => void;
}) {
  return (
    <Menu
      title="Account, appearance and sign out"
      width={286}
      trigger={
        <>
          <span className="grid size-[22px] place-items-center rounded-full bg-[var(--accent-soft)] text-[10.5px] font-semibold text-[var(--accent)]">
            {user.initials}
          </span>
          <span className="hidden flex-col leading-[1.15] text-left sm:flex">
            <span className="text-[12.5px] font-medium whitespace-nowrap">{user.name}</span>
            {user.jobTitle && (
              <span className="text-[10.5px] whitespace-nowrap text-[var(--ink-3)]">
                {user.jobTitle}
              </span>
            )}
          </span>
        </>
      }
    >
      {}
      {roles && activeRole && onRoleChange && (
        <>
          <div className="bb-card-head">
            <span className="bb-eyebrow text-[10px]">Signed in as</span>
          </div>
          <div className="max-h-[210px] overflow-y-auto">
            {roles.map((role) => (
              <MenuOption
                key={role.value}
                selected={role.value === activeRole}
                onSelect={() => onRoleChange(role.value)}
                label={role.label}
                hint={role.hint}
              />
            ))}
          </div>
        </>
      )}

      <div className="border-t border-[var(--line-2)] bg-[var(--surface-2)]">
        {}
        <MenuSegment
          label="Theme"
          value={theme}
          onChange={onThemeChange}
          options={[
            { value: 'light' as ThemeMode, label: 'Light' },
            { value: 'dark' as ThemeMode, label: 'Dark' },
          ]}
        />
        {}
        <MenuSegment
          label="Density"
          value={density}
          onChange={onDensityChange}
          options={[
            { value: 'comfortable' as Density, label: 'Comfortable', hint: '40px rows' },
            { value: 'compact' as Density, label: 'Compact', hint: '31px rows — more per screen' },
          ]}
        />
      </div>

      {onSignOut && (
        <button
          type="button"
          onClick={onSignOut}
          className="flex w-full cursor-pointer items-center gap-2.5 border-0 border-t border-[var(--line-2)] bg-transparent px-3 py-2.5 text-left text-[12.5px] font-medium text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M15 17l5-5-5-5" />
            <path d="M20 12H9" />
            <path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
          </svg>
          Sign out
        </button>
      )}
    </Menu>
  );
}


export interface SideRailProps {
  moduleLabel: string;
  items: NavItem[];
  activeItem: string;
  onItemChange: (key: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  modules?: NavModule[];
  activeModule?: string;
  onModuleChange?: (key: string) => void;
  overlay?: boolean;
}

function glyphFor(item: NavItem): string {
  if (item.tag) return item.tag;
  const initials = item.label
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .join('');
  return initials.slice(0, 2).toUpperCase();
}

export function SideRail({
  moduleLabel,
  items,
  activeItem,
  onItemChange,
  collapsed,
  onToggleCollapse,
  modules,
  activeModule,
  onModuleChange,
  overlay = false,
}: SideRailProps) {
  return (
    <aside
      data-noprint="1"
      className={cn(
        'shrink-0 overflow-hidden border-r border-[var(--line)] bg-[var(--surface)]',
        'transition-[width] duration-[var(--dur)] ease-[var(--ease)]',
        overlay &&
          'max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:shadow-[var(--shadow-lg)]',
      )}
      style={{ width: collapsed ? 'var(--rail-w-collapsed)' : 'var(--rail-w)' }}
    >
      <div
        className={cn(
          'flex h-full flex-col gap-0.5 overflow-x-hidden overflow-y-auto',
          collapsed ? 'w-[var(--rail-w-collapsed)] px-2 py-3' : 'w-[var(--rail-w)] p-3',
        )}
      >
        <div
          className={cn(
            'flex items-center pb-2.5',
            collapsed ? 'justify-center' : 'justify-between px-2',
          )}
        >
          {!collapsed && (
            <span className="bb-eyebrow overflow-hidden text-[10px] tracking-[0.14em] text-ellipsis whitespace-nowrap">
              {moduleLabel}
            </span>
          )}
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand navigation' : 'Collapse to tags'}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!collapsed}
            className="cursor-pointer border-0 bg-transparent p-0.5 leading-none text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              {collapsed ? (
                <>
                  <path d="M11 6l6 6-6 6" />
                  <path d="M6 6v12" />
                </>
              ) : (
                <>
                  <path d="M13 6l-6 6 6 6" />
                  <path d="M18 6v12" />
                </>
              )}
            </svg>
          </button>
        </div>

        {}
        {modules && activeModule && onModuleChange && !collapsed && (
          <nav aria-label="Modules" className="flex flex-col gap-0.5 lg:hidden">
            <span className="bb-eyebrow px-2.5 pb-1 text-[10px] tracking-[0.14em]">Modules</span>
            {modules.map((m) => {
              const active = m.key === activeModule;
              const className = cn(
                'flex h-[31px] w-full cursor-pointer items-center rounded-[var(--radius-sm)] border-0 px-2.5 text-left text-[12.5px] no-underline',
                active
                  ? 'bg-[var(--surface-2)] font-semibold text-[var(--ink)]'
                  : 'bg-transparent font-normal text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink-2)]',
              );

              return m.href ? (
                <a
                  key={m.key}
                  href={m.href}
                  title={m.hint}
                  aria-current={active ? 'page' : undefined}
                  onClick={(event) => {
                    if (!isPlainClick(event)) return;
                    event.preventDefault();
                    onModuleChange(m.key);
                  }}
                  className={className}
                >
                  {m.label}
                </a>
              ) : (
                <button
                  key={m.key}
                  type="button"
                  title={m.hint}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onModuleChange(m.key)}
                  className={className}
                >
                  {m.label}
                </button>
              );
            })}
            <div className="my-2 h-px bg-[var(--line-2)]" />
            <span className="bb-eyebrow px-2.5 pb-1 text-[10px] tracking-[0.14em]">
              {moduleLabel}
            </span>
          </nav>
        )}

        {items.map((item, i) => {
          const active = item.key === activeItem;

          const link = (className: string, children: ReactNode, label?: string) =>
            item.href ? (
              <a
                key={item.key}
                href={item.href}
                title={label}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  onItemChange(item.key);
                }}
                className={cn(className, 'no-underline')}
                style={{ animationDelay: `${i * 0.018}s` }}
              >
                {children}
              </a>
            ) : (
              <button
                key={item.key}
                type="button"
                title={label}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                onClick={() => onItemChange(item.key)}
                className={className}
                style={{ animationDelay: `${i * 0.018}s` }}
              >
                {children}
              </button>
            );

          if (collapsed) {
            return link(
              cn(
                'bb-sweep-in relative grid h-[34px] w-full cursor-pointer place-items-center rounded-[var(--radius-sm)] border-0',
                active
                  ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'bg-transparent text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
              ),
              <>
                <span className="bb-mono text-[10.5px] font-semibold tracking-tight">
                  {glyphFor(item)}
                </span>
                {item.badge ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-1 right-1 size-[6px] rounded-full bg-[var(--warn)]"
                  />
                ) : null}
              </>,
              item.label,
            );
          }

          return link(
            cn(
              'bb-sweep-in flex h-[33px] w-full cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] border-0 px-2.5 text-left text-[13px]',
              active
                ? 'bg-[var(--accent-soft)] font-semibold text-[var(--accent)]'
                : 'bg-transparent font-normal text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
            ),
            <>
              <span
                aria-hidden="true"
                className="size-[5px] shrink-0 rounded-full bg-current opacity-50"
              />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {item.label}
              </span>
              {item.badge ? (
                <span className="bb-num rounded-full bg-[var(--warn-soft)] px-1.5 text-[10px] text-[var(--warn)]">
                  {item.badge}
                </span>
              ) : item.tag ? (
                <Tag>{item.tag}</Tag>
              ) : null}
            </>,
          );
        })}
      </div>
    </aside>
  );
}


export interface LetterheadProps {
  name: string;
  legalName?: string;
  logoUrl?: string;
  location?: string;
  phone?: string;
  email?: string;
  ntn?: string;
  strn?: string;
  printedAt?: string;
}

function Letterhead({
  name,
  legalName,
  logoUrl,
  location,
  phone,
  email,
  ntn,
  strn,
  printedAt,
}: LetterheadProps) {
  const contact = [phone, email].filter(Boolean).join(' · ');
  const statutory = [ntn && `NTN ${ntn}`, strn && `STRN ${strn}`].filter(Boolean).join(' · ');

  return (
    <div className="hidden pb-4 print:block">
      <div className="flex items-start justify-between gap-6 border-b border-black pb-2">
        <div className="flex items-start gap-3">
          {logoUrl && <img src={logoUrl} alt="" className="h-[38px] w-auto object-contain" />}
          <div>
            <div className="text-[15px] font-semibold">{name}</div>
            {legalName && legalName !== name && <div className="text-[11px]">{legalName}</div>}
            {location && <div className="text-[11px]">{location}</div>}
            {contact && <div className="text-[11px]">{contact}</div>}
          </div>
        </div>
        <div className="text-right text-[10.5px]">
          {statutory && <div>{statutory}</div>}
          {printedAt && <div>Printed {printedAt}</div>}
        </div>
      </div>
    </div>
  );
}


export function PageHeader({
  breadcrumb,
  title,
  description,
  actions,
  meta,
}: {
  breadcrumb?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div data-report-head="1" className="flex items-end justify-between gap-6 pb-5">
      <div className="min-w-0">
        {breadcrumb && <div className="bb-eyebrow pb-2">{breadcrumb}</div>}
        <h1 className="m-0 text-[25px] leading-tight font-semibold tracking-tight text-[var(--ink)]">
          {title}
        </h1>
        {description && (
          <p className="mt-2 mb-0 max-w-[90ch] text-[13px] text-[var(--ink-2)] text-pretty">
            {description}
          </p>
        )}
        {meta && <div className="flex flex-wrap items-center gap-2 pt-3">{meta}</div>}
      </div>
      {actions && (
        <div data-noprint="1" className="flex shrink-0 items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}


export interface AppShellProps extends Omit<TopBarProps, 'modules'> {
  modules: NavModule[];
  activeItem: string;
  onItemChange: (key: string) => void;
  busy?: boolean;
  letterhead?: LetterheadProps;
  children: ReactNode;
}

export function AppShell({
  modules,
  activeModule,
  onModuleChange,
  activeItem,
  onItemChange,
  busy = false,
  letterhead,
  children,
  ...topBarProps
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  const current = modules.find((m) => m.key === activeModule) ?? modules[0];

  const handleModuleChange = (key: string) => {
    if (key === activeModule) {
      setCollapsed((c) => !c);
      return;
    }
    setCollapsed(false);
    onModuleChange(key);
  };

  const isOverlaid = () => typeof window !== 'undefined' && window.innerWidth < 1024;

  const handleItemChange = (key: string) => {
    onItemChange(key);
    if (isOverlaid()) setCollapsed(true);
  };

  return (
    <div data-app="1" className="bb-fade-in flex h-screen flex-col overflow-hidden bg-[var(--bg)]">
      <TopBar
        modules={modules}
        activeModule={activeModule}
        onModuleChange={handleModuleChange}
        {...topBarProps}
      />

      <div className="relative flex min-h-0 flex-1">
        <SideRail
          moduleLabel={current?.label ?? ''}
          items={current?.items ?? []}
          activeItem={activeItem}
          onItemChange={handleItemChange}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          modules={modules}
          activeModule={activeModule}
          onModuleChange={onModuleChange}
          overlay={!collapsed}
        />

        {}
        {!collapsed && (
          <button
            type="button"
            data-noprint="1"
            aria-label="Close navigation"
            onClick={() => setCollapsed(true)}
            className="absolute inset-0 z-20 cursor-default border-0 bg-[var(--bg)]/55 lg:hidden"
          />
        )}

        {}
        <div className="relative flex min-w-0 flex-1 flex-col">
          <RouteProgress active={busy} />

          <main className="min-w-0 flex-1 overflow-y-auto pb-14">
            {}
            <div
              key={activeItem}
              className="bb-page-in mx-auto max-w-[var(--content-max)] px-4 pt-7 sm:px-[30px]"
            >
              {letterhead && <Letterhead {...letterhead} />}
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
